import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  MoreHorizontal,
  UserPlus,
  ShieldOff,
  ShieldCheck,
  Trash2,
  Eye,
  Edit,
  KeyRound,
  BookOpen,
  UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/admin/DataTable";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import {
  getUsers,
} from "@/lib/admin/queries";
import {
  suspendUser,
  unsuspendUser,
  deleteUser,
  resetUserPassword,
  createUser,
  updateUser,
} from "@/lib/admin/actions";
import type { Profile } from "@/types/database";
import { useAuth } from "@/contexts/AuthContext";
import type { DataTableColumn } from "@/components/admin/DataTable";

type UserFiltersState = {
  search: string;
  role: string;
  plan: string;
  status: string;
  dateFrom: string;
  dateTo: string;
};

type ConfirmActionType =
  | { type: "suspend"; userId: string; email?: string }
  | { type: "unsuspend"; userId: string; email?: string }
  | { type: "delete"; userId: string; email?: string }
  | { type: "reset"; userId: string; email: string }
  | { type: "bulk-suspend"; ids: string[] }
  | { type: "bulk-delete"; ids: string[] }
  | null;

type CreateUserFormValues = {
  full_name: string;
  email: string;
  role: string;
  plan: string;
  sendInvite: boolean;
};

type EditUserFormValues = {
  full_name: string;
  email: string;
  role: string;
};

const ALL_VALUE = "__all__";

export default function Users() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";

  const [filters, setFilters] = useState<UserFiltersState>({
    search: "",
    role: "",
    plan: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmActionType>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters.search]);

  const queryFilters = {
    search: debouncedSearch,
    role: filters.role || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
  };

  const usersQuery = useQuery({
    queryKey: ["admin", "users", queryFilters, page, pageSize],
    queryFn: () => getUsers(queryFilters, page, pageSize),
  });

  const refetchUsers = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "users"] });

  // Mutations
  const suspendMutation = useMutation({
    mutationFn: (id: string) => suspendUser(id, actorId),
    onSuccess: () => { toast.success("User suspended"); refetchUsers(); },
    onError: () => toast.error("Failed to suspend user"),
  });
  const unsuspendMutation = useMutation({
    mutationFn: (id: string) => unsuspendUser(id, actorId),
    onSuccess: () => { toast.success("User unsuspended"); refetchUsers(); },
    onError: () => toast.error("Failed to unsuspend user"),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id, actorId),
    onSuccess: () => { toast.success("User deleted"); refetchUsers(); },
    onError: () => toast.error("Failed to delete user"),
  });
  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => resetUserPassword(email),
    onSuccess: () => toast.success("Password reset email sent"),
    onError: () => toast.error("Failed to send reset email"),
  });
  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserFormValues) =>
      createUser({ ...data }, actorId),
    onSuccess: () => {
      toast.success("User created");
      setShowCreateModal(false);
      createForm.reset();
      refetchUsers();
    },
    onError: () => toast.error("Failed to create user"),
  });
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditUserFormValues }) =>
      updateUser(id, data, actorId),
    onSuccess: () => {
      toast.success("User updated");
      setEditingUser(null);
      refetchUsers();
    },
    onError: () => toast.error("Failed to update user"),
  });

  const createForm = useForm<CreateUserFormValues>({
    defaultValues: {
      full_name: "",
      email: "",
      role: "host",
      plan: "",
      sendInvite: true,
    },
  });

  const editForm = useForm<EditUserFormValues>();

  useEffect(() => {
    if (editingUser) {
      editForm.reset({
        full_name: editingUser.full_name,
        email: editingUser.email,
        role: editingUser.role,
      });
    }
  }, [editingUser, editForm]);

  async function handleConfirm() {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      if (confirmAction.type === "suspend") {
        await suspendMutation.mutateAsync(confirmAction.userId);
      } else if (confirmAction.type === "unsuspend") {
        await unsuspendMutation.mutateAsync(confirmAction.userId);
      } else if (confirmAction.type === "delete") {
        await deleteMutation.mutateAsync(confirmAction.userId);
      } else if (confirmAction.type === "reset" && confirmAction.email) {
        await resetPasswordMutation.mutateAsync(confirmAction.email);
      } else if (confirmAction.type === "bulk-suspend") {
        await Promise.all(confirmAction.ids.map((id) => suspendUser(id, actorId)));
        toast.success(`${confirmAction.ids.length} users suspended`);
        setSelectedRows([]);
        refetchUsers();
      } else if (confirmAction.type === "bulk-delete") {
        await Promise.all(confirmAction.ids.map((id) => deleteUser(id, actorId)));
        toast.success(`${confirmAction.ids.length} users deleted`);
        setSelectedRows([]);
        refetchUsers();
      }
    } finally {
      setConfirmLoading(false);
      setConfirmAction(null);
    }
  }

  function exportCsv() {
    const rows = usersQuery.data?.data ?? [];
    const headers = ["ID", "Name", "Email", "Role", "Joined"];
    const csv = [
      headers.join(","),
      ...rows.map((u) =>
        [
          u.id,
          `"${u.full_name}"`,
          u.email,
          u.role,
          u.created_at.slice(0, 10),
        ].join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  const columns: DataTableColumn<Profile & Record<string, unknown>>[] = [
    {
      key: "id",
      label: "ID",
      render: (row) => (
        <span className="text-xs font-mono text-gray-400">{(row.id as string).slice(0, 8)}</span>
      ),
    },
    {
      key: "full_name",
      label: "Name",
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate max-w-[160px]">{row.full_name as string}</p>
          <p className="text-xs text-gray-400 truncate max-w-[160px]">{row.email as string}</p>
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      render: (row) => <span className="text-gray-600 truncate max-w-[160px] block">{row.email as string}</span>,
    },
    {
      key: "role",
      label: "Role",
      render: (row) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 whitespace-nowrap">
          {String(row.role).replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: () => <StatusBadge status="active" size="sm" />,
    },
    {
      key: "created_at",
      label: "Joined",
      sortable: true,
      render: (row) => (
        <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
          {new Date(row.created_at as string).toLocaleDateString("en-CA")}
        </span>
      ),
    },
    {
      key: "updated_at",
      label: "Last Active",
      render: (row) => (
        <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
          {new Date(row.updated_at as string).toLocaleDateString("en-CA")}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const userId = row.id as string;
        const email = row.email as string;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open user actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="whitespace-nowrap" onClick={() => navigate(`/admin/users/${userId}`)}>
                <Eye className="h-4 w-4 mr-2" /> View profile
              </DropdownMenuItem>
              <DropdownMenuItem className="whitespace-nowrap" onClick={() => setEditingUser(row as unknown as Profile)}>
                <Edit className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem className="whitespace-nowrap" onClick={() => navigate(`/admin/users/${userId}?tab=bookings`)}>
                <BookOpen className="h-4 w-4 mr-2" /> View bookings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => setConfirmAction({ type: "reset", userId, email })}
              >
                <KeyRound className="h-4 w-4 mr-2" /> Reset password
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() =>
                  setConfirmAction({ type: "suspend", userId, email })
                }
              >
                <ShieldOff className="h-4 w-4 mr-2" /> Suspend
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() =>
                  setConfirmAction({ type: "unsuspend", userId, email })
                }
              >
                <ShieldCheck className="h-4 w-4 mr-2" /> Unsuspend
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="whitespace-nowrap text-red-600 focus:text-red-700 focus:bg-red-50"
                onClick={() =>
                  setConfirmAction({ type: "delete", userId, email })
                }
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const filterBar = (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={filters.role || ALL_VALUE}
        onValueChange={(v) => { setFilters((f) => ({ ...f, role: v === ALL_VALUE ? "" : v })); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-36 text-sm">
          <SelectValue placeholder="All Roles" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Roles</SelectItem>
          <SelectItem value="strata_admin">Strata Admin</SelectItem>
          <SelectItem value="property_manager">Property Manager</SelectItem>
          <SelectItem value="host">Host</SelectItem>
          <SelectItem value="individual">Individual</SelectItem>
          <SelectItem value="system_admin">System Admin</SelectItem>
        </SelectContent>
      </Select>
      <Input
        type="date"
        className="h-9 w-36 text-sm"
        value={filters.dateFrom}
        onChange={(e) => { setFilters((f) => ({ ...f, dateFrom: e.target.value })); setPage(1); }}
        aria-label="From date"
      />
      <Input
        type="date"
        className="h-9 w-36 text-sm"
        value={filters.dateTo}
        onChange={(e) => { setFilters((f) => ({ ...f, dateTo: e.target.value })); setPage(1); }}
        aria-label="To date"
      />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <div className="flex items-center gap-2">
          {selectedRows.length > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="whitespace-nowrap"
                onClick={() => setConfirmAction({ type: "bulk-suspend", ids: selectedRows })}
              >
                <ShieldOff className="h-4 w-4 mr-1.5" />
                Suspend ({selectedRows.length})
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="whitespace-nowrap"
                onClick={() => setConfirmAction({ type: "bulk-delete", ids: selectedRows })}
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete ({selectedRows.length})
              </Button>
              <Button variant="outline" size="sm" className="whitespace-nowrap" onClick={exportCsv}>
                Export CSV
              </Button>
            </>
          )}
          <Button size="sm" className="whitespace-nowrap" onClick={() => setShowCreateModal(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Create User
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <DataTable
            columns={columns as DataTableColumn[]}
            data={(usersQuery.data?.data ?? []) as Record<string, unknown>[]}
            loading={usersQuery.isLoading}
            totalCount={usersQuery.data?.count}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            onSearch={(q) => setFilters((f) => ({ ...f, search: q }))}
            searchPlaceholder="Search by name or email…"
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            onExport={exportCsv}
            filters={filterBar}
            emptyMessage="No users found."
          />
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={createForm.handleSubmit((data) => createUserMutation.mutate(data))}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="cu-name" className="whitespace-nowrap">Full Name <span className="text-red-500">*</span></Label>
              <Input id="cu-name" {...createForm.register("full_name", { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cu-email" className="whitespace-nowrap">Email <span className="text-red-500">*</span></Label>
              <Input id="cu-email" type="email" {...createForm.register("email", { required: true })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cu-role">Role</Label>
                <Select
                  value={createForm.watch("role")}
                  onValueChange={(v) => createForm.setValue("role", v)}
                >
                  <SelectTrigger id="cu-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="host">Host</SelectItem>
                    <SelectItem value="strata_admin">Strata Admin</SelectItem>
                    <SelectItem value="property_manager">Property Manager</SelectItem>
                    <SelectItem value="individual">Individual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cu-plan">Plan</Label>
                <Select
                  value={createForm.watch("plan")}
                  onValueChange={(v) => createForm.setValue("plan", v)}
                >
                  <SelectTrigger id="cu-plan">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    <SelectItem value="strata">Strata CA$18.99/mo</SelectItem>
                    <SelectItem value="property_manager">PM CA$28.99/mo</SelectItem>
                    <SelectItem value="individual">Individual CA$9.99/mo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="cu-invite"
                checked={createForm.watch("sendInvite")}
                onCheckedChange={(v) => createForm.setValue("sendInvite", v === true)}
              />
              <Label htmlFor="cu-invite" className="font-normal">Send invite email</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createUserMutation.isPending}>
                {createUserMutation.isPending ? "Creating…" : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Sheet */}
      <Sheet open={!!editingUser} onOpenChange={(open) => { if (!open) setEditingUser(null); }}>
        <SheetContent className="w-[400px] sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Edit User</SheetTitle>
          </SheetHeader>
          <form
            onSubmit={editForm.handleSubmit((data) => {
              if (!editingUser) return;
              updateUserMutation.mutate({ id: editingUser.id, data });
            })}
            className="mt-6 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="eu-name" className="whitespace-nowrap">Full Name <span className="text-red-500">*</span></Label>
              <Input id="eu-name" {...editForm.register("full_name", { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eu-email" className="whitespace-nowrap">Email <span className="text-red-500">*</span></Label>
              <Input id="eu-email" type="email" {...editForm.register("email", { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eu-role" className="whitespace-nowrap">Role</Label>
              <Select
                value={editForm.watch("role")}
                onValueChange={(v) => editForm.setValue("role", v)}
              >
                <SelectTrigger id="eu-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="host">Host</SelectItem>
                  <SelectItem value="strata_admin">Strata Admin</SelectItem>
                  <SelectItem value="property_manager">Property Manager</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="system_admin">System Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditingUser(null)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={updateUserMutation.isPending}>
                {updateUserMutation.isPending ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Confirm Modal */}
      <ConfirmModal
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        loading={confirmLoading}
        title={
          confirmAction?.type === "delete" || confirmAction?.type === "bulk-delete"
            ? "Delete User(s)"
            : confirmAction?.type === "suspend" || confirmAction?.type === "bulk-suspend"
            ? "Suspend User(s)"
            : confirmAction?.type === "reset"
            ? "Reset Password"
            : "Confirm Action"
        }
        description={
          confirmAction?.type === "delete"
            ? `Permanently delete user ${confirmAction.email ?? ""}? This cannot be undone.`
            : confirmAction?.type === "bulk-delete"
            ? `Permanently delete ${(confirmAction as { ids: string[] }).ids.length} selected users? This cannot be undone.`
            : confirmAction?.type === "suspend"
            ? `Suspend ${confirmAction.email ?? "this user"}? They will lose access.`
            : confirmAction?.type === "bulk-suspend"
            ? `Suspend ${(confirmAction as { ids: string[] }).ids.length} selected users?`
            : confirmAction?.type === "unsuspend"
            ? `Unsuspend ${confirmAction.email ?? "this user"}? They will regain access.`
            : `Send password reset email to ${confirmAction?.email ?? "this user"}?`
        }
        requireTyping={
          confirmAction?.type === "delete" || confirmAction?.type === "bulk-delete"
        }
        variant={
          confirmAction?.type === "delete" || confirmAction?.type === "bulk-delete"
            ? "destructive"
            : "warning"
        }
      />
    </div>
  );
}
